/**
 * pdfCache.ts
 *
 * Store-agnostic PDF rendering and dimension cache.
 *
 * Extracted from BackgroundRenderer.tsx so that both the React component
 * layer (BackgroundRenderer) AND the store layer (playerStore) can import
 * these utilities without creating a circular dependency — BackgroundRenderer
 * imports globalStore, so playerStore must not import BackgroundRenderer.
 *
 * Usage:
 *   import { preloadPdfBackground, getPdfPageDimensions } from '../helper/pdfCache';
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import * as pdfjs from 'pdfjs-dist';
import { getSharedPdfWorker } from './polyfill';
// Two-tier cache so the first render pays for fetch+render exactly once per
// session and subsequent mounts read the blob URL synchronously.
//   pdfBlobUrlPromises: in-flight promises (dedupes concurrent fetches)
//   pdfBlobUrlResolved: resolved blob URLs (hot path for page re-entry)
var pdfBlobUrlPromises = new Map();
var pdfBlobUrlResolved = new Map();
// Rasterised page dimensions at the DISPLAY scale. The blob image itself is
// rendered at a higher RENDER scale so the browser can downsample for crisp
// edges and text on high-density displays — the cached dimensions describe
// what the consumer should lay out, not the pixel count in the PNG.
var pdfPageDimensions = new Map();
// Display scale: the size at which the rasterised PDF is laid out in the
// canvas (matches what we shipped before).
var PDF_DISPLAY_SCALE = 2;
// Render multiplier on top of display: rasterise the PNG larger than the
// display size so the browser can downsample for sharpness instead of
// upscaling a blurry source. Scales with the device pixel ratio (capped at
// 2× for memory safety) and multiplied by 1.5 for an extra crispness margin:
//   • standard display (DPR=1): 1.5× → effective render = 3× display
//   • retina (DPR=2):           3.0× → effective render = 6× display
var PDF_RENDER_MULTIPLIER = (typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1) * 1.5;
function renderPdfToBlobUrl(file) {
    return __awaiter(this, void 0, void 0, function () {
        var res, arrayBuffer, fileData, pdf, page, displayViewport, renderViewport, canvas_1, url, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, fetch(file).catch(function () { return null; })];
                case 1:
                    res = _a.sent();
                    if (!res || !res.ok)
                        return [2 /*return*/, null];
                    _a.label = 2;
                case 2:
                    _a.trys.push([2, 8, , 9]);
                    return [4 /*yield*/, res.arrayBuffer()];
                case 3:
                    arrayBuffer = _a.sent();
                    fileData = new Uint8Array(arrayBuffer);
                    return [4 /*yield*/, pdfjs.getDocument({
                            data: fileData,
                            worker: getSharedPdfWorker(),
                            // pdfjs 5.x ships JPX (JPEG 2000) and JBIG2 decoders as WASM modules
                            // served from disk. Without this URL pointing to the copied bundle,
                            // PDFs that use JPX soft masks render with the masked image dropped
                            // entirely (the illustration disappears even though the rest renders).
                            wasmUrl: '/js/wasm/',
                        }).promise];
                case 4:
                    pdf = _a.sent();
                    return [4 /*yield*/, pdf.getPage(1)];
                case 5:
                    page = _a.sent();
                    displayViewport = page.getViewport({ scale: PDF_DISPLAY_SCALE });
                    renderViewport = page.getViewport({
                        scale: PDF_DISPLAY_SCALE * PDF_RENDER_MULTIPLIER,
                    });
                    // Cache the DISPLAY dimensions — consumers (bgOffset, fit-zoom) work in
                    // layout coords, not canvas-pixel coords.
                    pdfPageDimensions.set(file, {
                        width: displayViewport.width,
                        height: displayViewport.height,
                    });
                    canvas_1 = document.createElement('canvas');
                    canvas_1.width = renderViewport.width;
                    canvas_1.height = renderViewport.height;
                    // ENABLE_STORAGE flattens interactive form widgets (text fields,
                    // checkboxes, signatures, etc.) into the rasterised image. The default
                    // (ENABLE) renders markup annotations but treats form widgets as display-
                    // layer overlays — so anything authored as a form field is "visible in
                    // the source PDF but missing in the activity". pdfjs docs explicitly
                    // recommend ENABLE_STORAGE for print-style rasterisation.
                    return [4 /*yield*/, page.render({
                            canvas: canvas_1,
                            viewport: renderViewport,
                            annotationMode: pdfjs.AnnotationMode.ENABLE_STORAGE,
                        }).promise];
                case 6:
                    // ENABLE_STORAGE flattens interactive form widgets (text fields,
                    // checkboxes, signatures, etc.) into the rasterised image. The default
                    // (ENABLE) renders markup annotations but treats form widgets as display-
                    // layer overlays — so anything authored as a form field is "visible in
                    // the source PDF but missing in the activity". pdfjs docs explicitly
                    // recommend ENABLE_STORAGE for print-style rasterisation.
                    _a.sent();
                    return [4 /*yield*/, new Promise(function (resolve) {
                            canvas_1.toBlob(function (b) { return resolve(b ? URL.createObjectURL(b) : null); }, 'image/png');
                        })];
                case 7:
                    url = _a.sent();
                    if (url)
                        pdfBlobUrlResolved.set(file, url);
                    return [2 /*return*/, url];
                case 8:
                    error_1 = _a.sent();
                    // Report before swallowing. Returning null keeps the caller's contract (a
                    // failed background is not a fatal error), but the bare `catch` used to
                    // make every failure invisible: nothing reached `window`, so the
                    // `?debug=1` field overlay listed no errors at all and a blank background
                    // was indistinguishable from an activity that simply has none. That is
                    // what hid `UnknownErrorException: s.toHex is not a function` — pdf.js
                    // calling a Chrome-140 API on older engines (see
                    // src/polyfills/uint8ArrayBase64.js) — on real classroom devices.
                    console.error("[pdfCache] failed to render PDF background \"".concat(file, "\""), error_1);
                    return [2 /*return*/, null];
                case 9: return [2 /*return*/];
            }
        });
    });
}
/**
 * Fetch, render, and cache a PDF as a blob URL.
 * Subsequent calls for the same `file` are deduplicated — the same promise
 * is returned while in-flight and the resolved blob URL is returned instantly
 * once cached.
 */
export function preloadPdfBackground(file) {
    if (!file)
        return Promise.resolve(null);
    var resolved = pdfBlobUrlResolved.get(file);
    if (resolved)
        return Promise.resolve(resolved);
    var inFlight = pdfBlobUrlPromises.get(file);
    if (inFlight)
        return inFlight;
    var promise = renderPdfToBlobUrl(file).catch(function () { return null; });
    pdfBlobUrlPromises.set(file, promise);
    // Evict the in-flight slot on failure so a retry can actually re-attempt.
    promise.then(function (url) {
        pdfBlobUrlPromises.delete(file);
        if (url === null)
            pdfBlobUrlResolved.delete(file);
    });
    return promise;
}
/** Return the cached blob URL for a PDF, or undefined if not yet rendered. */
export function getCachedPdfBackground(file) {
    return pdfBlobUrlResolved.get(file);
}
/**
 * Return the rasterised dimensions of the first page of a PDF (at scale 2),
 * or undefined if the PDF has not been preloaded yet.
 */
export function getPdfPageDimensions(file) {
    return pdfPageDimensions.get(file);
}
